import React from "react";
import AuthenticityTokenField from "../../../components/forms/AuthenticityTokenField";
import { Button } from "../../../components/ui/button";
import { SelectInput } from "../../../components/ui/FormControls";
import type { MainAppPageProps, MembershipRole, OrgMember } from "../types";

type MemberRoleFormProps = Pick<MainAppPageProps, "csrfToken" | "routes"> & {
  member: OrgMember;
  actorRole: MembershipRole;
};

function memberRoleAction(
  template: string | undefined,
  membershipId: string,
  fallback: string,
) {
  return (template ?? fallback).replace(":id", membershipId);
}

function assignableRoles(actorRole: MembershipRole): MembershipRole[] {
  if (actorRole === "owner") {
    return ["admin", "member", "viewer"];
  }
  if (actorRole === "admin") {
    return ["member", "viewer"];
  }
  return [];
}

export const MemberRoleForm: React.FC<MemberRoleFormProps> = ({
  csrfToken,
  routes,
  member,
  actorRole,
}) => {
  const roles = assignableRoles(actorRole);
  if (member.role === "owner" || roles.length === 0) {
    return null;
  }
  if (member.role === "admin" && actorRole !== "owner") {
    return null;
  }

  const action = memberRoleAction(
    routes.updateOrganizationMemberRole,
    member.id,
    `/organization/members/${member.id}`,
  );

  return (
    <form action={action} method="post" className="flex items-center gap-2">
      <AuthenticityTokenField csrfToken={csrfToken} />
      <input type="hidden" name="_method" value="patch" />
      <SelectInput
        id={`member_role_${member.id}`}
        name="membership[role]"
        defaultValue={member.role}
        className="h-9 min-w-[7rem] capitalize"
        aria-label={`Change role for ${member.name}`}
      >
        {roles.map((role) => (
          <option key={role} value={role} className="capitalize">
            {role}
          </option>
        ))}
      </SelectInput>
      <Button type="submit" variant="outline" size="sm">
        Save
      </Button>
    </form>
  );
};
